<template>
  <div class="LabPartners">
    <div class="LabPartners-header">
      <h1 class="LabPartners-title">Lab Partners</h1>
      <button
        class="Button LabPartners-refreshButton"
        @click="loadPartners"
        :disabled="loading">
        Refresh
      </button>
    </div>

    <p class="LabPartners-intro">
      Track referral commission balances. Partners with at least £{{ minPayout }} accrued may be eligible for Wednesday manual payouts.
    </p>

    <div class="LabPartners-filters">
      <div class="LabPartners-filterGroup">
        <label for="lab-partner-status-filter">Status</label>
        <select id="lab-partner-status-filter" v-model="statusFilter" @change="loadPartners">
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
        </select>
      </div>
      <div class="LabPartners-filterGroup">
        <label>
          <input type="checkbox" v-model="showPayoutCandidatesOnly">
          Show payout candidates only (£{{ minPayout }}+)
        </label>
      </div>
    </div>

    <div v-if="loading" class="LabPartners-loading">
      <p>Loading Lab Partners…</p>
    </div>

    <div v-if="error" class="LabPartners-error">
      <p>{{ error }}</p>
    </div>

    <div v-if="success" class="LabPartners-success">
      <p>{{ success }}</p>
    </div>

    <div class="FlexTable" v-if="displayPartners.length > 0">
      <div class="FlexTable-header">
        <h3 class="FlexTable-title">
          {{ displayPartners.length }} Lab Partner<span v-if="displayPartners.length !== 1">s</span>
        </h3>
      </div>
      <ve-table
        class="LabPartners-table"
        row-key-field-name="id"
        :fixed-header="true"
        :columns="tableColumns"
        :table-data="displayPartners"
        :cell-style-option="cellStyleOption"
      />
    </div>

    <div v-else-if="!loading" class="FlexTable">
      <div class="FlexTable-header">
        <h3 class="FlexTable-title">No Lab Partners found</h3>
      </div>
      <p class="FlexTable-content FlexTable-contentItem">
        No partners match the current filters.
      </p>
    </div>
  </div>
</template>

<script>
import './LabPartners.scss';
import axios from 'axios';
import cookieModel from '../../models/cookie';
import errorModel from '../../models/error';
import {
  LAB_PARTNER_MIN_PAYOUT,
  filterPayoutCandidates,
  isAccruedAboveThreshold,
  setAuthHeader,
  fetchLabPartners,
} from '../../models/lab-partners';

export default {
  name: 'LabPartners',
  mounted() {
    if (this.$route.query.payout_candidates === '1') {
      this.showPayoutCandidatesOnly = true;
    }
    this.loadPartners();
  },
  computed: {
    displayPartners() {
      if (!this.showPayoutCandidatesOnly) {
        return this.partners;
      }

      return filterPayoutCandidates(this.partners, this.minPayout);
    },
  },
  methods: {
    loadPartners: function () {
      const vm = this;
      vm.loading = true;
      vm.error = '';
      vm.success = '';

      setAuthHeader(vm.axiosInstance, cookieModel);

      const params = {
        per_page: 100,
      };

      if (vm.statusFilter) {
        params.status = vm.statusFilter;
      }

      fetchLabPartners(vm.axiosInstance, vm.baseUrl, params)
        .then(function (response) {
          vm.partners = (response.data && response.data.data) ? response.data.data : [];
          vm.loading = false;
        })
        .catch(function (error) {
          vm.error = errorModel.apiError(error, vm.$router);
          vm.loading = false;
        });
    },

    viewPartner: function (partnerId) {
      this.$router.push({
        name: 'LabPartnerDetail',
        params: { id: partnerId },
      });
    },
  },
  data: function () {
    const vm = this;

    return {
      baseUrl: process.env.VUE_APP_API_URL,
      axiosInstance: axios.create({
        baseURL: process.env.VUE_APP_API_URL,
      }),
      loading: false,
      error: '',
      success: '',
      partners: [],
      statusFilter: 'active',
      showPayoutCandidatesOnly: false,
      minPayout: LAB_PARTNER_MIN_PAYOUT,
      cellStyleOption: {
        bodyCellClass: () => '',
      },
      tableColumns: [
        {
          field: 'code',
          key: 'code',
          title: 'Code',
          align: 'left',
        },
        {
          field: 'name',
          key: 'name',
          title: 'Name',
          align: 'left',
        },
        {
          field: 'email',
          key: 'email',
          title: 'Email',
          align: 'left',
        },
        {
          field: 'status',
          key: 'status',
          title: 'Status',
          align: 'left',
        },
        {
          field: 'accrued_total',
          key: 'accrued_total',
          title: 'Accrued (£)',
          align: 'right',
          /* eslint-disable no-unused-vars */
          renderBodyCell: ({ row }, h) => {
            const ready = isAccruedAboveThreshold(row, vm.minPayout);
            return h(
              'span',
              {
                class: ready ? 'LabPartners-amount LabPartners-amount--ready' : 'LabPartners-amount',
              },
              row.accrued_total
            );
          },
        },
        {
          field: 'user_id',
          key: 'user_id',
          title: 'User linked',
          align: 'center',
          renderBodyCell: ({ row }, h) => h('span', {}, row.user_id ? 'Yes' : 'No'),
        },
        {
          field: '',
          key: 'actions',
          title: 'Actions',
          align: 'left',
          renderBodyCell: ({ row }, h) => h(
            'button',
            {
              class: 'Button',
              on: {
                click: () => vm.viewPartner(row.id),
              },
            },
            'View payout'
          ),
        },
      ],
    };
  },
};
</script>
